import { brand } from './brand';
import { normalizeStorefrontSettings } from './storefrontComposition';
import { appPathWithTenant, isTenantSubdomainHost } from './tenantHost';

/** Settings App|Site switch (`storefront.homeEnabled`) — public face is the website. */
export function isWebsiteStorefront(
  tenant: {
    policies?: { storefront?: unknown };
    publicShowcase?: boolean;
  } | null | undefined,
): boolean {
  // Alive Album has no App/Site switch. The purchased site is the public home.
  if (brand.id === 'alivealbum') return tenant?.publicShowcase === true;
  return normalizeStorefrontSettings(tenant?.policies?.storefront).homeEnabled === true;
}

/**
 * Public vitrine inside `/app` (catalog chrome). Cabinet routes are not included.
 * Staff `/app/profile` is the cabinet profile, not the vitrine.
 */
export function isAppVitrinePath(pathname: string, isTenantStaff: boolean): boolean {
  const p = String(pathname || '').replace(/\/+$/, '') || '/';
  if (
    p === '/app/catalog'
    || p === '/app/cart'
    || p === '/app/favorites'
    || p === '/app/news'
    || p === '/app/about'
  ) {
    return true;
  }
  return p === '/app/profile' && !isTenantStaff;
}

function copyPublicSearch(fromSearch: string, slug?: string | null): URLSearchParams {
  const src = new URLSearchParams(fromSearch.startsWith('?') ? fromSearch.slice(1) : fromSearch);
  const dst = new URLSearchParams();
  const tenant = src.get('tenant') || (!isTenantSubdomainHost() && slug ? slug : null);
  if (tenant) dst.set('tenant', tenant);
  const product = src.get('product');
  if (product) dst.set('product', product);
  const lang = src.get('lang');
  if (lang) dst.set('lang', lang);
  return dst;
}

/** `/app/catalog?product=` → website `/` with the same public query (+ optional section scroll). */
export function toWebsiteLocation(
  search: string,
  slug?: string | null,
  fromPathname?: string,
): {
  pathname: '/';
  search: string;
  /** Consumed by TenantWebsiteShell — avoid #hashes (shell strips them on load). */
  state?: { storefrontScrollTo: string };
} {
  const qs = copyPublicSearch(search, slug).toString();
  const p = String(fromPathname || '').replace(/\/+$/, '') || '/';
  let storefrontScrollTo: string | undefined;
  if (p === '/app/catalog') {
    storefrontScrollTo = 'website-module-catalog';
  } else if (p === '/app/about') {
    storefrontScrollTo = 'website-module-about';
  } else if (p === '/app/profile') {
    storefrontScrollTo = 'website-module-bio';
  }
  return {
    pathname: '/',
    search: qs ? `?${qs}` : '',
    ...(storefrontScrollTo ? { state: { storefrontScrollTo } } : {}),
  };
}

export function toWebsitePath(search: string, slug?: string | null, fromPathname?: string): string {
  const loc = toWebsiteLocation(search, slug, fromPathname);
  return `${loc.pathname}${loc.search}`;
}

/** `/?product=` → `/app/catalog?product=` when the public face is the app. */
export function toAppCatalogPath(search: string, slug?: string | null): string {
  const copied = copyPublicSearch(search, slug);
  const tenant = copied.get('tenant');
  copied.delete('tenant');
  const base = appPathWithTenant('/app/catalog', slug || tenant);
  const [pathname, existing = ''] = base.split('?');
  const merged = new URLSearchParams(existing);
  copied.forEach((value, key) => merged.set(key, value));
  const qs = merged.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}
